<template>
  <div>
    <div class="alert alert-danger" role="alert" v-if="loginError">
      <span>{{ loginError }}</span>
    </div>
    <div class="login-card">
      <div>
        <form
          data-ts="Form"
          @submit.prevent="login"
          v-if="!samlUrl && !show2faPrompt && !show2faSetup"
        >
          <fieldset>
            <label>
              <span>Email</span>
              <input
                @blur="validateEmail"
                @keyup="checkSaml"
                autofocus
                name="email"
                type="email"
                required
                v-model="email"
              />
            </label>
            <dl class="ts-errors" v-if="emailError">
              <dt>Not a valid email</dt>
            </dl>
          </fieldset>

          <div v-if="!samlUrl">
            <fieldset>
              <label>
                <span>Password</span>
                <input
                  autofocus
                  name="password"
                  type="password"
                  v-model="password"
                  required
                />
              </label>
            </fieldset>
          </div>
          <div class="buttons">
            <button
              v-if="!samlUrl"
              :disabled="!validInput"
              data-ts="Button"
              type="submit"
              class="ts-secondary button"
            >
              <span>Login</span>
            </button>
            <a
              v-if="okta"
              id="oktaButton"
              href="/okta"
              data-ts="Button"
              type="button"
              class="ts-tertiary ts-button"
            >
              <span>OKTA SSO</span>
            </a>
          </div>
        </form>

        <form v-if="samlUrl">
          <div class="buttons">
            <a
              v-if="samlUrl"
              :href="samlUrl"
              data-ts="Button"
              type="button"
              class="ts-secondary ts-button"
            >
              <span>Tradeshift SSO</span>
            </a>
          </div>
        </form>

        <div v-if="show2faSetup && !samlUrl">
          <two-factor-setup
            :verify-code="complete2faSetup"
            :secret="secret"
            :qr-code-svg="qrCodeSvg"
          />
        </div>

        <div
          v-if="show2faPrompt && !samlUrl"
          style="display: flex; flex-direction: column; align-items: center"
        >
          <h6>Please Enter Two-Factor Authentication Code</h6>
          <two-factor ref="twoFactorCodeRef" @submit="verify2fa"></two-factor>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import axios from 'axios';
import { ref, computed } from 'vue';
import TwoFactor from './TwoFactor.vue';
import TwoFactorSetup from './TwoFactorSetup.vue';
import { notify } from '../../utils';

const props = defineProps({
  ssoError: {
    type: String,
    default: null,
  },
  okta: {
    type: Boolean,
    default: false,
  },
  smalHost: {
    type: String,
    required: true,
  },
});

const loginError = ref(props.ssoError);
const email = ref(null);
const password = ref(null);
const emailError = ref(false);
const samlUrl = ref(null);
const twoFactorCodeRef = ref(null);
const show2faSetup = ref(false);
const show2faPrompt = ref(false);
const qrCodeSvg = ref('');
const secret = ref('');

const validInput = computed(() => {
  return validateEmailAddress(email.value) && password.value;
});

function validateEmail() {
  if (email.value) {
    emailError.value = !validateEmailAddress(email.value);
  } else {
    emailError.value = null;
  }
}

const loginSpinner = (on) => {
  const main = document.querySelector('#app > main');
  if (on) {
    main.setAttribute('data-ts.blocking', 'Logging in...');
  } else {
    main.removeAttribute('data-ts.blocking');
  }
};

const validateEmailAddress = (emailAddress) => {
  const pattern = /^[^@ \t\r\n]+@[^@ \t\r\n]+\.[^@ \t\r\n]{2,}$/i;
  return pattern.test(emailAddress);
};

const checkSaml = async () => {
  if (!validateEmailAddress(email.value)) {
    samlUrl.value = null;
    return;
  }

  const res = await fetch(`${props.smalHost}/idp/${email.value}`);
  const data = await res.json();

  samlUrl.value = data.loginUrl;

  if (samlUrl.value?.startsWith(props.smalHost)) {
    if (samlUrl.value.indexOf('?') === -1) {
      samlUrl.value += '?relayState=juvo';
    } else {
      samlUrl.value += '&relayState=juvo';
    }
  }
};

const login = async () => {
  if (email.value && password.value) {
    loginSpinner(true);

    try {
      const response = await axios.post('/authorization', {
        email: email.value,
        password: password.value,
      });

      if (response.data.status === '2fa_setup') {
        show2faSetup.value = true;
        qrCodeSvg.value = response.data.qrCodeSvg;
        secret.value = response.data.secret;
      } else if (response.data.status === '2fa_required') {
        show2faPrompt.value = true;
      }
    } catch (error) {
      loginError.value = error.response.data.message || 'An error occurred';
      ts.ui.Notification.error(loginError.value);
    } finally {
      loginSpinner(false);
    }
  }
};

const verify2fa = async (code) => {
  loginSpinner(true);
  try {
    const response = await axios.post('/authorization', {
      email: email.value,
      password: password.value,
      twoFactorCode: code,
    });

    if (response.data.status === 'success') {
      window.location.href = '/support/search';
    } else if (response.data.status === '2fa_failed') {
      loginError.value = response.data.message;
    }
  } catch (error) {
    loginError.value = error.response.data.message || 'An error occurred';
    if (twoFactorCodeRef.value) {
      twoFactorCodeRef.value.clear();
    }
  } finally {
    loginSpinner(false);
  }
};

const complete2faSetup = async (code) => {
  loginSpinner(true);
  try {
    const response = await axios.post('/authorization', {
      email: email.value,
      password: password.value,
      twoFactorCode: code,
      twoFactorSecret: secret.value,
    });

    if (response.data.status === 'success') {
      notify.info('Two-Factor Authentication setup complete.', {
        onaccept: () => {
          window.location.href = '/support/search';
        },
      });
    } else if (response.data.status === '2fa_failed') {
      loginError.value = response.data.message;
      if (twoFactorCodeRef.value) {
        twoFactorCodeRef.value.clear();
      }
    }
  } catch (error) {
    loginError.value = error.response.data.message || 'An error occurred';
    if (twoFactorCodeRef.value) {
      twoFactorCodeRef.value.clear();
    }
  } finally {
    loginSpinner(false);
  }

  // show2faSetup.value = false;
  // show2faPrompt.value = true;
};
</script>

<style>
label {
  margin-bottom: 0;
}

#oktaButton {
  margin-top: 10px;
}

.ts-button {
  width: 100%;
}

.buttons {
  padding: 20px 10px;
}

.login-card {
  width: 400px;
  display: block;
  margin: auto;
  padding: 15px;
  background-color: #fff;
  -webkit-box-shadow: 0 2px 2px rgba(0, 0, 0, 0.1),
    0 2px 8px rgba(0, 0, 0, 0.05);
  box-shadow: 0 2px 2px rgba(0, 0, 0, 0.1), 0 2px 8px rgba(0, 0, 0, 0.05);
  border: 1px solid #cbd7dc;
}

.alert {
  width: 400px;
  display: block;
  padding: 15px;
  background-color: #bf0201;
  -webkit-box-shadow: 0 2px 2px rgba(0, 0, 0, 0.1),
    0 2px 8px rgba(0, 0, 0, 0.05);
  box-shadow: 0 2px 2px rgba(0, 0, 0, 0.1), 0 2px 8px rgba(0, 0, 0, 0.05);
  border: 1px solid #cbd7dc;
  color: white;
  text-align: center;
  margin: 15px auto;
}
</style>
